// Thanks to https://paladini.dev/posts/how-to-make-an-extremely-reusable-tooltip-component-with-react--and-nothing-else/ for inspiration!
import { Transition } from '@headlessui/react';
import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';

export type TooltipProps = {
    /**
     * The content of the tooltip
     */
    message: string | string[] | { content: string; icon?: ReactNode }[];

    /**
     * Which side of the content to display on
     */
    position?: 'top' | 'right' | 'bottom' | 'left';

    /**
     * Prevents the tooltip from showing
     */
    disabled?: boolean;

    /**
     * Delays the activation of the tooltip
     */
    delayMs?: number;

    /**
     * Content the tooltip will be wrapped around
     */
    children: ReactNode | ReactNode[];
};

const styles = {
    bubble: {
        right: 'left-[102%] bottom-1/2 translate-y-1/2',
        left: 'right-[102%] bottom-1/2 translate-y-1/2',
        top: 'bottom-[102%] right-1/2 translate-x-1/2',
        bottom: 'right-1/2 translate-x-1/2',
    },
    triangle: {
        right: 'border-r-gray-800/[0.85]',
        left: 'border-l-gray-800/[0.85]',
        top: 'border-t-gray-800/[0.85]',
        bottom: 'border-b-gray-800/[0.85]',
    },
};

function Tooltip({ children, message, position: side = 'right', disabled = false, delayMs = 250 }: TooltipProps): JSX.Element {
    const timeout = useRef<NodeJS.Timeout | null>(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        return (): void => {
            clearTimeout(timeout.current);
            setShow(false);
        };
    }, []);

    function handleEnter(): void {
        timeout.current = setTimeout(() => {
            setShow(true);
        }, delayMs);
    }

    function handleLeave(): void {
        setShow(false);
        clearTimeout(timeout.current);
    }
    if (disabled) {
        return <>{children}</>;
    }
    return (
        <div onMouseEnter={handleEnter} onMouseLeave={handleLeave} className="relative w-fit">
            {children}
            <Transition
                as={Fragment}
                show={show}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-85"
                leave="transition-opacity duration-150"
                leaveFrom="opacity-85"
                leaveTo="opacity-0"
            >
                <div className={`absolute ${styles.bubble[side]} w-max max-w-[35ch] break-words`} style={{ zIndex: 999 }}>
                    <div className={`relative flex items-center ${side === 'top' || side === 'bottom' ? 'flex-col' : ''}`}>
                        {(side === 'right' || side === 'bottom') && (
                            <div className={`w-0 h-0 border-[6px] border-transparent ${styles.triangle[side]}`}></div>
                        )}
                        <div className="bg-gray-800/[0.85] text-white text-xs p-2 rounded">
                            {Array.isArray(message) &&
                                message.map((m, idx) => {
                                    return (
                                        <>
                                            {typeof m === 'string' ? (
                                                <p key={idx} className="leading-5">
                                                    {m}
                                                </p>
                                            ) : (
                                                <div key={idx} className="flex gap-2 items-center">
                                                    <p className="leading-5">{m.content}</p>
                                                    {m.icon}
                                                </div>
                                            )}
                                        </>
                                    );
                                })}
                            {typeof message === 'string' && <p>{message}</p>}
                        </div>
                        {(side === 'left' || side === 'top') && (
                            <div className={`w-0 h-0 border-[6px] border-transparent ${styles.triangle[side]}`}></div>
                        )}
                    </div>
                </div>
            </Transition>
        </div>
    );
}

export default Tooltip;
